/**
 * Client-side tools for AI agent threads.
 *
 * Each tool lives in its own file and exports a {@link ClientTool}.
 * To add a new client tool:
 *
 * 1. Create a new file in this directory (e.g., `myTool.ts`)
 * 2. Define an input type and implement execute
 * 3. Export a `ClientTool` (directly or via {@link createClientTool})
 * 4. Import it and add it to the `ALL_TOOLS` list below
 *
 * See `navigate.ts` for a full example with dependencies and parameterized routing.
 */
import {
  type EventModel,
  hasSessionEvents,
  legacyEventsWritable,
} from "@/shared/domain/orgs";

import { tool as addToCollectionTool } from "./addToCollection";
import { tool as applyViewTool } from "./applyView";
import { tool as clientCreateAgentTool } from "./clientCreateAgent";
import {
  ToolErrorKind,
  confirmationContentFor,
  createClientTool,
  type ClientTool,
  type ClientToolConfig,
  type ClientToolDeps,
  type ClientToolExecutionResult,
  type ConfirmationContentProps,
} from "./ClientTools";
import { tool as createBoardTool } from "./createBoard";
import { tool as createCollectionTool } from "./createCollection";
import { tool as createEventsTool } from "./createEvents";
import { tool as createSessionEventsTool } from "./createSessionEvents";
import { tool as createSkillTool } from "./createSkill";
import { tool as dryRunBoardTool } from "./dryRunBoard";
import { tool as navigateTool } from "./navigate";
import { tool as publishBoardClauseTool } from "./publishBoardClause";
import { tool as readBoardTool } from "./readBoard";
import { tool as readPanelDraftTool } from "./readPanelDraft";
import { tool as updateBoardTool } from "./updateBoard";
import { tool as updatePanelDraftTool } from "./updatePanelDraft";
import {
  addToPanelTool,
  applyLayoutTool,
  openFileTool,
  openPanelTool,
  removeFromPanelTool,
  removePanelTool,
  searchTopicTool,
  seekTool,
  setLogPanelFilterTool,
} from "./visualize";

export { ToolErrorKind, confirmationContentFor, createClientTool };
export type {
  ClientTool,
  ClientToolConfig,
  ClientToolDeps,
  ClientToolExecutionResult,
  ConfirmationContentProps,
};

/**
 * Every client tool. Each name appears once: the model provider rejects a
 * request that declares two tools under one name.
 */
const ALL_TOOLS: readonly ClientTool[] = [
  navigateTool,
  createEventsTool,
  createSessionEventsTool,
  createCollectionTool,
  addToCollectionTool,
  createSkillTool,
  createBoardTool,
  dryRunBoardTool,
  updateBoardTool,
  readBoardTool,
  readPanelDraftTool,
  updatePanelDraftTool,
  publishBoardClauseTool,
  clientCreateAgentTool,
  applyViewTool,
  openPanelTool,
  addToPanelTool,
  removeFromPanelTool,
  removePanelTool,
  searchTopicTool,
  openFileTool,
  seekTool,
  applyLayoutTool,
  setLogPanelFilterTool,
];

const BOARD_TOOLS: ReadonlySet<ClientTool> = new Set([
  createBoardTool,
  dryRunBoardTool,
  updateBoardTool,
  readBoardTool,
  readPanelDraftTool,
  updatePanelDraftTool,
  publishBoardClauseTool,
]);

/**
 * What decides which client tools a person is offered: whether boards are
 * released to them (the `release-boards` flag), and the current org's event
 * model (undefined while its record loads).
 */
export interface ClientToolAvailability {
  boardsReleased: boolean;
  eventModel: EventModel | undefined;
}

/** Every client tool, less the board tools when boards are not released. */
function releasedTools(boardsReleased: boolean): readonly ClientTool[] {
  if (boardsReleased) {
    return ALL_TOOLS;
  }
  return ALL_TOOLS.filter((tool) => !BOARD_TOOLS.has(tool));
}

/**
 * Whether an org on `eventModel` is offered `tool`: the dataset-event tool
 * while the org's dataset events can be created, the session-event tool once
 * the org has session events, every other tool always. While the event model
 * loads, only the dataset-event tool is offered, since an org's event model
 * defaults to `legacy`.
 */
function offeredByEventModel(
  tool: ClientTool,
  eventModel: EventModel | undefined,
): boolean {
  if (tool === createEventsTool) {
    return eventModel === undefined || legacyEventsWritable(eventModel);
  }
  if (tool === createSessionEventsTool) {
    return hasSessionEvents(eventModel);
  }
  return true;
}

/**
 * The tool modules a person is offered, for the settings modal: the tools
 * released to them, with only the event tools the current org can use.
 */
export function clientToolModules({
  boardsReleased,
  eventModel,
}: ClientToolAvailability): readonly ClientTool[] {
  return releasedTools(boardsReleased).filter((tool) =>
    offeredByEventModel(tool, eventModel),
  );
}

/**
 * The specs of the tools {@link clientToolModules} returns, sent to the
 * backend with every thread request so the LLM knows these tools are
 * available.
 */
export function browserClientTools(availability: ClientToolAvailability) {
  return clientToolModules(availability).map((tool) => tool.spec);
}

/**
 * True when `toolName` names one of the web app's client tools, released to
 * the person or not.
 */
export function isClientToolName(toolName: string): boolean {
  return ALL_TOOLS.some((tool) => tool.spec.name === toolName);
}

/**
 * Find the tool named `toolName` among the tools released to the person.
 * Returns undefined for an unknown name, and for a board tool when Boards is
 * not released to the person. Unlike {@link clientToolModules}, it resolves
 * both event tools whatever the org's event model: a call can predate the
 * model loading, and each event tool refuses an event its org cannot create.
 *
 * Resolves the tool without running it. Tools run through
 * `ClientToolBatchRunner`, which applies `toolCallNeedsApproval` to every
 * tool_use it runs: several client tools (`client_create_collection`,
 * `client_add_to_collection`) save records and send no idempotency key, so
 * each run has to pass the person's approval mode.
 */
export function getClientTool(
  toolName: string,
  released: { boardsReleased: boolean },
): ClientTool | undefined {
  return releasedTools(released.boardsReleased).find(
    (tool) => tool.spec.name === toolName,
  );
}
